import { useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import MultiLineWidget from 'components/Widgets/MultiLineChart';
import IncidentFilter from 'components/Dropdown/IncidentFilter';
import { fetchIncidentTrend, fetchMetricsTechnologyList } from 'service/service';
import dayjs from 'dayjs';
import CustomDropdown from 'components/Dropdown';
import moment from 'moment';

function ITSMOpsTrend(props: any) {
    const [incidentsChartOptions, SetIncidentsChartOptions] = useState<Record<string, any>>({});
    const [incidentsChartLoader, SetIncidentsChartLoader] = useState<boolean>(true);
    const [technology, setTechnology] = useState('all');
    const [technologyList, setTechnologyList] = useState<any[]>([]);
    const [chartType, setChartype] = useState('User Incidents');
    const chartTypes = ['User Incidents', 'Automated Incidents', 'Change Requests', 'Service Requests', 'Problem Tickets']
    
    const getMonthLabel = (yearMonth: string) => {
        return dayjs(`${yearMonth}-01`).format("MMM YYYY");
    };

    const buildIncidentChartOption = (
        incidentsData: any[],
        avgData: any[],
        chartType: string
    ): Record<string, any> => {
        const incidents = incidentsData.map((item: any) => item.incidentCount);
        const avgValue = avgData[0]?.avg ?? 0;
        const avg2024Incidents = incidentsData.map(() => avgValue);
        const dates = incidentsData.map((item: any) => getMonthLabel(item.yearMonth));

        const allValues = [...incidents, ...avg2024Incidents];
        const maxVal = Math.max(...allValues);
        const minVal = Math.min(...allValues);
        const yMax = Math.ceil(maxVal / 10) * 10;
        const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

        return {
            backgroundColor: '#003771',
            tooltip: {
                trigger: 'axis',
                textStyle: { color: '#A7ADCB' },
                confine: true,
                backgroundColor: '#003771',
                formatter: function (params: any[]) {
                    const date = params[0]?.axisValueLabel;
                    let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                    params.forEach((item) => {
                        tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                    });
                    return tooltipContent;
                }
            },
            grid: { top: '7%' },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: { color: '#A7ADCB' },
                splitLine: { show: false }
            },
            yAxis: {
                type: 'value',
                min: yMin,
                max: yMax,
                axisLabel: {
                    color: '#A7ADCB',
                    formatter: (value: number) => (value >= 1000 ? `${value / 1000}k` : value)
                },
                splitLine: { show: false }
            },
            series: [
                {
                    name: chartType,
                    type: 'line',
                    data: incidents,
                    symbol: 'circle',
                    color: '#009BD8',
                    lineStyle: { width: 3 },
                    label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                    areaStyle: {
                        color: {
                            type: 'linear', x: 0, y: 1, x2: 0, y2: 0,
                            colorStops: [{ offset: 0, color: '#053367' }, { offset: 1, color: '#009BD8' }]
                        }
                    }
                },
                {
                    name: `${moment(props.filterMonth).year()-1} Average ${chartType}`,
                    label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                    type: 'line',
                    data: avg2024Incidents,
                    symbol: 'none',
                    color: '#FFC239',
                    lineStyle: { width: 2 }
                }
            ],
            legend: {
                orient: 'horizontal', bottom: '1%', left: '2%',
                textStyle: { color: '#A7ADCB' },
                selectedMode: false,
                itemWidth: 10, itemHeight: 10, icon: 'roundRect'
            }
        };
    };

    const fetchTechnologyList = async () => {
        try {
            SetIncidentsChartLoader(true);
            const res = await fetchMetricsTechnologyList({ metricsType: props.metricsType, isClosedDate: props.isClosedDate });
            if (res.success) {
                const result = res.data.result.map((item: any) => item.technology)
                setTechnologyList(result);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        }
    };

    const fetchIncidentsChartData = async (technology: string, chartType: string, sector: string, isClosedDate: any, filterMonth: any) => {
        try {
            SetIncidentsChartLoader(true);
            const res = await fetchIncidentTrend({ metricsType: props.metricsType, technology, chartType, sector, isClosedDate, dateFilter: filterMonth });
            if (res.success) {
                const options = buildIncidentChartOption(res.data.currentYearIncident, res.data.lastYearIncident, chartType);
                SetIncidentsChartOptions(options);
            }
        } catch (error) {
            console.error('Error fetching incident trend data:', error);
        } finally {
            SetIncidentsChartLoader(false);
        }
    };

    useEffect(() => {
        fetchTechnologyList();
    }, []);

    useEffect(() => {
        fetchIncidentsChartData(technology, chartType, props.sectorName, props.isClosedDate, props.filterMonth);
    }, [chartType, technology, props.sectorName, props.metricsType, props.isClosedDate, props.filterMonth]);


    const handleTechnologyChange = (selectedTechnology: string) => {
        fetchIncidentsChartData(selectedTechnology, chartType, props.sectorName, props.isClosedDate, props.filterMonth);
        setTechnology(selectedTechnology); 
    };

    const handleChartTypeChange = (selectedChartType: string) => {
        fetchIncidentsChartData(technology, selectedChartType, props.sectorName, props.isClosedDate, props.filterMonth);
        setChartype(selectedChartType); 
    }

    return (        
        <Grid item xs={12} md={6} lg={6}>
            <MultiLineWidget
                title={props.title || ""}
                isLoading={incidentsChartLoader}
                data={incidentsChartOptions}
                hasFilter={true}
            >
                <CustomDropdown
                    handleChange={(event) => {
                        setChartype(event.target.value)
                        handleChartTypeChange(event.target.value)
                    }}
                    value={chartType}
                    options={chartTypes.map((item: string) => ({ label: item, value: item }))}
                />
                <IncidentFilter
                    sectorName={technology}
                    setSectorName={setTechnology}
                    sectorOptionsList={technologyList}
                    handleChange={handleTechnologyChange}
                    type={props.chartLabel}
                    disabled={false}
                    boxProps={{ sx: { minWidth: '20px', position: 'static' } }}
                />
            </MultiLineWidget>
        </Grid>
    );
}

export default ITSMOpsTrend;